import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { StoredPrompt } from '../../../shared/types/context'
import { api } from '../../lib/api'
import { TextContentViewer } from '../TextContentViewer'
import { TASK_DRAG_TYPE } from '../../../shared/lib/dragTypes'

const QUICK_TASK_STORAGE_KEY = 'tasks-panel-quick-input'

type TaskPrompt = StoredPrompt & { snippet: string }

const formatSnippet = (content: string, maxLength = 200): string => {
  const normalized = content.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) {
    return normalized
  }
  return `${normalized.slice(0, maxLength - 1)}…`
}

const formatTimestamp = (value: number | null | undefined): string => {
  if (!value) {
    return 'never'
  }
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return 'unknown'
    }
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return 'unknown'
  }
}

const generateEditorSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function TasksPanel() {
  const [tasks, setTasks] = useState<TaskPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editorSessionId, setEditorSessionId] = useState(() => generateEditorSessionId())
  const [editorError, setEditorError] = useState<string | null>(null)
  const [quickTaskInput, setQuickTaskInput] = useState(() => {
    try {
      return localStorage.getItem(QUICK_TASK_STORAGE_KEY) || ''
    } catch {
      return ''
    }
  })
  const [isSavingQuickTask, setIsSavingQuickTask] = useState(false)
  const mountedRef = useRef(true)
  const quickTaskDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const quickInputRef = useRef<HTMLTextAreaElement | null>(null)
  const taskListRef = useRef<HTMLUListElement | null>(null)
  const draggingTaskRef = useRef<string | null>(null)
  const [taskDropIndex, setTaskDropIndex] = useState<number | null>(null)
  const [isDestroyTargetActive, setIsDestroyTargetActive] = useState(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (quickTaskDebounceRef.current) {
        clearTimeout(quickTaskDebounceRef.current)
      }
    }
  }, [])

  // Debounced localStorage save for quick task input
  useEffect(() => {
    if (quickTaskDebounceRef.current) {
      clearTimeout(quickTaskDebounceRef.current)
    }

    quickTaskDebounceRef.current = setTimeout(() => {
      try {
        localStorage.setItem(QUICK_TASK_STORAGE_KEY, quickTaskInput)
      } catch (err) {
        console.error('[TasksPanel] Failed to save quick task to localStorage:', err)
      }
    }, 300)

    return () => {
      if (quickTaskDebounceRef.current) {
        clearTimeout(quickTaskDebounceRef.current)
      }
    }
  }, [quickTaskInput])

  const mapTasks = useCallback((promptList: StoredPrompt[]): TaskPrompt[] => {
    return promptList
      .filter((prompt) => prompt.type === 'task')
      .map((prompt) => ({
        ...prompt,
        snippet: formatSnippet(prompt.content),
      }))
  }, [])

  const loadTasks = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const list = await api().services.context.listPrompts()
      if (!mountedRef.current) {
        return
      }
      setTasks(mapTasks(list ?? []))
    } catch (err) {
      console.error('[TasksPanel] Failed to load tasks:', err)
      if (mountedRef.current) {
        setError('Failed to load tasks')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [mapTasks])

  useEffect(() => {
    void loadTasks()
  }, [loadTasks])

  const filteredTasks = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return tasks
    }
    return tasks.filter((task) => task.content.toLowerCase().includes(query))
  }, [tasks, searchQuery])

  const handleOpenEditor = () => {
    setEditorError(null)
    setIsEditorOpen(true)
    setEditorSessionId(generateEditorSessionId())
  }

  const handleCloseEditor = () => {
    setIsEditorOpen(false)
    setEditorError(null)
  }

  const handleTaskSave = useCallback(
    async (content: string) => {
      const trimmed = content.trim()
      if (!trimmed) {
        setEditorError('Enter task details before saving')
        throw new Error('Task content required')
      }
      setEditorError(null)
      try {
        await api().services.context.createPrompt(trimmed, { type: 'task' })
        await loadTasks()
        if (!mountedRef.current) {
          return
        }
        setIsEditorOpen(false)
        setEditorSessionId(generateEditorSessionId())
      } catch (err) {
        console.error('[TasksPanel] Failed to save task:', err)
        setEditorError('Failed to save task')
        throw err
      }
    },
    [loadTasks],
  )

  const handleQuickTaskSave = useCallback(
    async (value?: string) => {
      const raw = typeof value === 'string' ? value : quickTaskInput
      const trimmed = raw.trim()
      if (!trimmed || isSavingQuickTask) {
        return
      }
      setIsSavingQuickTask(true)
      setError(null)
      try {
        await api().services.context.createPrompt(trimmed, { type: 'task' })
        await loadTasks()
        if (!mountedRef.current) {
          return
        }
        setQuickTaskInput('')
        try {
          localStorage.removeItem(QUICK_TASK_STORAGE_KEY)
        } catch (err) {
          console.error('[TasksPanel] Failed to clear localStorage:', err)
        }
      } catch (err) {
        console.error('[TasksPanel] Failed to save quick task:', err)
        if (mountedRef.current) {
          setError('Failed to save task')
        }
      } finally {
        if (mountedRef.current) {
          setIsSavingQuickTask(false)
          // Focus textarea after state updates
          setTimeout(() => {
            const textarea = quickInputRef.current
            if (textarea) {
              textarea.focus()
            }
          }, 0)
        }
      }
    },
    [quickTaskInput, isSavingQuickTask, loadTasks],
  )

  const handleQuickTaskKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') {
        return
      }
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return
      }
      event.preventDefault()
      const nextValue = quickTaskInput.trim()
      if (!nextValue) {
        return
      }
      void handleQuickTaskSave(nextValue)
    },
    [handleQuickTaskSave, quickTaskInput],
  )

  const handleTaskDragStart = (event: React.DragEvent<HTMLLIElement>, task: TaskPrompt) => {
    draggingTaskRef.current = task.hash
    setTaskDropIndex(null)
    event.dataTransfer.setData(
      TASK_DRAG_TYPE,
      JSON.stringify({
        content: task.content,
        hash: task.hash,
      }),
    )
    event.dataTransfer.setData('text/plain', task.content)
    event.dataTransfer.effectAllowed = 'copyMove'
  }

  const handleTaskDragEnd = (event: React.DragEvent<HTMLLIElement>) => {
    event.dataTransfer.clearData(TASK_DRAG_TYPE)
    event.dataTransfer.clearData('text/plain')
    draggingTaskRef.current = null
    setTaskDropIndex(null)
  }

  const allowTaskReorderDrop = (event: React.DragEvent<HTMLElement>) => {
    if (!draggingTaskRef.current) {
      return false
    }
    const types = Array.from(event.dataTransfer?.types ?? [])
    if (!types.includes(TASK_DRAG_TYPE)) {
      return false
    }
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    return true
  }

  const findTaskDropIndex = (event: React.DragEvent<HTMLElement>) => {
    const container = taskListRef.current
    if (!container) {
      return 0
    }
    const items = Array.from(container.querySelectorAll<HTMLElement>('.tasks-list__item'))
    const { clientY } = event
    for (let index = 0; index < items.length; index += 1) {
      const rect = items[index].getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      if (clientY < midpoint) {
        return index
      }
    }
    return items.length
  }

  const reorderTaskByHash = useCallback((taskHash: string, beforeTaskHash: string | null) => {
    if (!taskHash) {
      return
    }
    setTasks((prev) => {
      const currentIndex = prev.findIndex((task) => task.hash === taskHash)
      if (currentIndex === -1) {
        return prev
      }
      const beforeIndex = beforeTaskHash ? prev.findIndex((task) => task.hash === beforeTaskHash) : prev.length
      let targetIndex = beforeIndex === -1 ? prev.length : beforeIndex
      if (targetIndex === currentIndex || targetIndex === currentIndex + 1) {
        return prev
      }
      const next = [...prev]
      const [moved] = next.splice(currentIndex, 1)
      if (!moved) {
        return prev
      }
      const adjustedIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex
      next.splice(Math.max(0, Math.min(next.length, adjustedIndex)), 0, moved)
      return next
    })
  }, [])

  const handleTaskListDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!allowTaskReorderDrop(event)) {
      return
    }
    const index = findTaskDropIndex(event)
    setTaskDropIndex(index)
  }

  const handleTaskListDragLeave = (event: React.DragEvent<HTMLElement>) => {
    const related = event.relatedTarget as Node | null
    if (related && event.currentTarget.contains(related)) {
      return
    }
    setTaskDropIndex(null)
  }

  const handleTaskListDrop = (event: React.DragEvent<HTMLElement>) => {
    if (!draggingTaskRef.current) {
      setTaskDropIndex(null)
      return
    }
    if (!allowTaskReorderDrop(event)) {
      setTaskDropIndex(null)
      return
    }
    event.preventDefault()
    event.stopPropagation()
    const targetIndex = findTaskDropIndex(event)
    const beforeTask = filteredTasks[targetIndex] ?? null
    reorderTaskByHash(draggingTaskRef.current, beforeTask?.hash ?? null)
    setTaskDropIndex(null)
  }

  const handleDestroyDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!draggingTaskRef.current) {
      return
    }
    const types = Array.from(event.dataTransfer?.types ?? [])
    if (!types.includes(TASK_DRAG_TYPE)) {
      return
    }
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setIsDestroyTargetActive(true)
  }

  const handleDestroyDragLeave = () => {
    setIsDestroyTargetActive(false)
  }

  const handleDestroyDrop = async (event: React.DragEvent<HTMLElement>) => {
    if (!draggingTaskRef.current) {
      setIsDestroyTargetActive(false)
      return
    }
    const types = Array.from(event.dataTransfer?.types ?? [])
    if (!types.includes(TASK_DRAG_TYPE)) {
      setIsDestroyTargetActive(false)
      return
    }
    event.preventDefault()
    event.stopPropagation()
    const taskHash = draggingTaskRef.current
    const task = tasks.find((t) => t.hash === taskHash)
    setIsDestroyTargetActive(false)
    draggingTaskRef.current = null
    if (!task) {
      return
    }
    try {
      await api().services.context.deletePrompt(task.content)
      setTasks((prev) => prev.filter((t) => t.hash !== taskHash))
    } catch (err) {
      console.error('[TasksPanel] Failed to delete task:', err)
      setError('Failed to delete task')
    }
  }

  const renderList = () => {
    if (loading) {
      return (
        <div className="empty-state">
          <p>Loading tasks…</p>
        </div>
      )
    }

    if (!filteredTasks.length) {
      return (
        <div className="empty-state">
          <p>No tasks yet</p>
          <p className="empty-state-subtitle">Create a task to assign it to an agent</p>
        </div>
      )
    }

    return (
      <ul
        className={`tasks-list${
          taskDropIndex !== null && taskDropIndex === filteredTasks.length ? ' tasks-list--drop-after' : ''
        }`}
        ref={taskListRef}
        onDragOver={handleTaskListDragOver}
        onDragLeave={handleTaskListDragLeave}
        onDrop={handleTaskListDrop}
      >
        {filteredTasks.map((task, index) => (
          <li
            key={task.hash}
            className={`task-card tasks-list__item${
              taskDropIndex === index ? ' tasks-list__item--drop-before' : ''
            }${taskDropIndex === index + 1 ? ' tasks-list__item--drop-after' : ''}`}
            draggable={true}
            onDragStart={(event) => handleTaskDragStart(event, task)}
            onDragEnd={handleTaskDragEnd}
          >
            <span className="task-card__title">{task.content}</span>
            <div className="task-card__meta">
              <span className="task-card__meta-text">{formatTimestamp(task.created_at)}</span>
            </div>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className="panel-shell tasks-panel">
      <div className="panel-controls">
        <button type="button" className="btn" onClick={handleOpenEditor}>
          New task
        </button>
        <button type="button" className="btn" onClick={() => void loadTasks()} disabled={loading}>
          Refresh
        </button>
        {error && <span className="panel-error">{error}</span>}
      </div>
      <div className="tasks-panel__quick-input">
        <textarea
          className="tasks-panel__quick-textarea"
          placeholder="Quick add task (Enter to save, Shift/Ctrl+Enter for newline)..."
          ref={quickInputRef}
          value={quickTaskInput}
          onChange={(e) => setQuickTaskInput(e.target.value)}
          onKeyDown={handleQuickTaskKeyDown}
          autoComplete="off"
          disabled={isSavingQuickTask}
          rows={5}
        />
        {quickTaskInput.trim() && (
          <div className="tasks-panel__quick-actions">
            <button
              type="button"
              className="btn"
              onClick={() => void handleQuickTaskSave()}
              disabled={isSavingQuickTask}
            >
              {isSavingQuickTask ? 'Saving...' : 'Save task'}
            </button>
          </div>
        )}
      </div>
      <div className="panel-content tasks-panel__content">
        {isEditorOpen ? (
          <div className="tasks-panel__editor">
            <div className="tasks-panel__editor-bar">
              <div>
                <p className="tasks-panel__editor-title">New task</p>
                <p className="tasks-panel__editor-hint">Use the editor to outline the work. Save to add it to the task list.</p>
              </div>
              <button type="button" className="btn" onClick={handleCloseEditor}>
                Close editor
              </button>
            </div>
            {editorError && <p className="tasks-panel__validation">{editorError}</p>}
            <TextContentViewer
              key={editorSessionId}
              content=""
              readOnly={false}
              enableWordWrap={true}
              initiallyEditing={true}
              onSave={handleTaskSave}
            />
          </div>
        ) : (
          <>
            <div className="tasks-panel__toolbar">
              <input
                type="text"
                className="tasks-panel__search"
                placeholder="Search tasks"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                aria-label="Search tasks"
              />
              <div className="tasks-panel__count">
                {filteredTasks.length} {filteredTasks.length === 1 ? 'task' : 'tasks'}
              </div>
            </div>
            <div className="tasks-panel__list">
              {renderList()}
            </div>
            <div
              className={`tasks-panel__destroy-target${isDestroyTargetActive ? ' tasks-panel__destroy-target--active' : ''}`}
              onDragOver={handleDestroyDragOver}
              onDragLeave={handleDestroyDragLeave}
              onDrop={handleDestroyDrop}
            >
              Drop here to delete
            </div>
          </>
        )}
      </div>
    </div>
  )
}
